import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FileText, Download, AlertCircle, CheckCircle2, Clock, Zap, Calendar } from 'lucide-react';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { SensitiveData } from '@/components/sensitive-data';
import { fetchWIPReports } from '@/lib/data-service';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { WIPReport } from '@/types';

import { useErrorHandler } from '@/hooks/use-error-handler';
import { useCompany } from '@/contexts/CompanyContext';

type ProjectTypeFilter = 'all' | 'time-and-materials' | 'fixed-price' | 'retainer';

const PROJECT_TYPE_LABELS: Record<string, string> = {
  'time-and-materials': 'T&M',
  'fixed-price': 'Fixed Fee',
  'retainer': 'Retainer',
};

function ProjectTypeBadge({ type }: { type: WIPReport['projectType'] }) {
  if (!type) return null;
  const label = PROJECT_TYPE_LABELS[type] ?? type;
  if (type === 'fixed-price') {
    return <Badge variant="default" className="text-xs whitespace-nowrap">{label}</Badge>;
  }
  if (type === 'retainer') {
    return <Badge variant="secondary" className="text-xs whitespace-nowrap">{label}</Badge>;
  }
  // T&M
  return <Badge variant="outline" className="text-xs whitespace-nowrap">{label}</Badge>;
}

function isBillingDueThisMonth(wip: WIPReport): boolean {
  // % complete milestones: the trigger is the completion threshold, not a date.
  // They count as "due this month" when the threshold is met (i.e. they are actionable now).
  if (wip.nextBillingEventTriggerType === 'percent-complete') {
    const thresholdMet =
      wip.nextBillingEventTriggerPercent != null &&
      (wip.projectType === 'fixed-price'); // completionPercent is on the project, not WIPReport — treat "has a due % milestone" as actionable
    // More precisely: the mock data only sets nextBillingEventTriggerType when the event is the next one;
    // readyToBill already captures whether it's actually due. Use that as the proxy.
    return thresholdMet && wip.readyToBill;
  }
  // Date-based milestone: check if the event date falls in the current calendar month
  if (!wip.nextBillingEvent) return false;
  const d = new Date(wip.nextBillingEvent);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

export function WIPReportingPage() {
  const [loading, setLoading] = useState(true);
  const [wipReports, setWipReports] = useState<WIPReport[]>([]);
  const [projectTypeFilter, setProjectTypeFilter] = useState<ProjectTypeFilter>('all');
  const { handleApiError } = useErrorHandler({ useToast: true });
  const { currentCompany } = useCompany();

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const data = await fetchWIPReports('wip-reporting');
        setWipReports(data);
      } catch (error) {
        handleApiError(error, '/api/wip-reports', 'GET');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [handleApiError, currentCompany?.id]);

  // Billing Due This Month banner data
  const billingDueThisMonth = useMemo(() => {
    const dueRows = wipReports.filter(w => isBillingDueThisMonth(w));
    const total = dueRows.reduce((sum, w) => sum + (w.nextBillingEventAmount ?? 0), 0);
    return { count: dueRows.length, total };
  }, [wipReports]);

  // Filtered rows
  const filteredReports = useMemo(() => {
    if (projectTypeFilter === 'all') return wipReports;
    return wipReports.filter(w => w.projectType === projectTypeFilter);
  }, [wipReports, projectTypeFilter]);

  // Summary figures (always from all rows, not filtered)
  const totalWIP = wipReports.reduce((sum, w) => sum + w.wipAmount, 0);
  const readyToBillRows = wipReports.filter(w => w.readyToBill);
  const readyToBillAmount = readyToBillRows.reduce((sum, w) => sum + w.wipAmount, 0);
  const totalUnderBilling = wipReports.reduce((sum, w) => sum + w.underBilling, 0);
  const totalOverBilling = wipReports.reduce((sum, w) => sum + w.overBilling, 0);

  const getAgingBadge = (days: number) => {
    if (days < 30) return <Badge variant="success">0-30 days</Badge>;
    if (days < 60) return <Badge variant="warning">30-60 days</Badge>;
    if (days < 90) return <Badge variant="warning" accent="orange">60-90 days</Badge>;
    return <Badge variant="destructive">90+ days</Badge>;
  };

  return (
    <div className="space-y-6">
      <PageHeaderBlock
        title="WIP"
        subtitle="Work In Progress analysis for month-end close"
        size={20}
        showActions={true}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => {/* Export to Excel - to be implemented */}}
            >
              <Download className="h-4 w-4 mr-2" />
              Export to Excel
            </Button>
            <Button onClick={() => {/* Generate Report - to be implemented */}}>
              <FileText className="h-4 w-4 mr-2" />
              Generate Report
            </Button>
          </>
        }
      />

      {/* Billing Due This Month Banner */}
      {!loading && billingDueThisMonth.count > 0 && (
        <div className="flex items-center justify-between gap-4 rounded-lg border border-warning-200 bg-warning-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <Zap className="h-5 w-5 shrink-0 text-warning-600" />
            <p className="text-sm font-medium text-warning-900">
              <span className="font-semibold">ACTION REQUIRED</span>
              {' — '}
              {billingDueThisMonth.count} billing event{billingDueThisMonth.count !== 1 ? 's' : ''} due this month totalling{' '}
              <SensitiveData>
                <span className="font-semibold">{formatCurrency(billingDueThisMonth.total)}</span>
              </SensitiveData>
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 border-warning-300 text-warning-800 hover:bg-warning-100"
            onClick={() => setProjectTypeFilter('fixed-price')}
          >
            View Due Items
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total WIP</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <SensitiveData>
                {formatCurrency(totalWIP)}
              </SensitiveData>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Unbilled work value
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Ready to Bill</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-success-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success-600">
              <SensitiveData>
                {formatCurrency(readyToBillAmount)}
              </SensitiveData>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {readyToBillRows.length} project{readyToBillRows.length !== 1 ? 's' : ''} ready for invoicing
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Under Billing</CardTitle>
            <AlertCircle className="h-4 w-4 text-warning-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <SensitiveData>
                {formatCurrency(totalUnderBilling)}
              </SensitiveData>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Work done, not billed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Over Billing</CardTitle>
            <AlertCircle className="h-4 w-4 text-error-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <SensitiveData>
                {formatCurrency(totalOverBilling)}
              </SensitiveData>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Billed ahead of work
            </p>
          </CardContent>
        </Card>
      </div>

      {/* WIP Table */}
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>WIP Analysis by Project</CardTitle>
          {/* Project Type Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Project type:</span>
            <Select
              value={projectTypeFilter}
              onValueChange={(v) => setProjectTypeFilter(v as ProjectTypeFilter)}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="time-and-materials">T&M</SelectItem>
                <SelectItem value="fixed-price">Fixed Fee</SelectItem>
                <SelectItem value="retainer">Retainer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Costs Incurred</TableHead>
                <TableHead className="text-right">Profit Earned</TableHead>
                <TableHead className="text-right">Amount Billed</TableHead>
                <TableHead className="text-right">WIP Amount</TableHead>
                <TableHead>
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Next Billing
                  </div>
                </TableHead>
                <TableHead>Aging</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-muted-foreground">
                    Loading WIP data...
                  </TableCell>
                </TableRow>
              ) : filteredReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                    No projects match the selected filter.
                  </TableCell>
                </TableRow>
              ) : (
                filteredReports.map(wip => (
                  <TableRow key={wip.projectId}>
                    <TableCell>
                      <div className="font-medium">{wip.projectName}</div>
                    </TableCell>
                    <TableCell>{wip.clientName}</TableCell>
                    <TableCell>
                      <ProjectTypeBadge type={wip.projectType} />
                    </TableCell>
                    <TableCell className="text-right">
                      <SensitiveData>
                        {formatCurrency(wip.costsIncurred)}
                      </SensitiveData>
                    </TableCell>
                    <TableCell className="text-right">
                      <SensitiveData>
                        {formatCurrency(wip.profitEarned)}
                      </SensitiveData>
                    </TableCell>
                    <TableCell className="text-right">
                      <SensitiveData>
                        {formatCurrency(wip.amountBilled)}
                      </SensitiveData>
                    </TableCell>
                    <TableCell className="text-right">
                      <SensitiveData>
                        <span
                          className={`font-semibold ${
                            wip.wipAmount > 0
                              ? 'text-warning-600'
                              : wip.wipAmount < 0
                              ? 'text-error-600'
                              : 'text-success-600'
                          }`}
                        >
                          {formatCurrency(wip.wipAmount)}
                        </span>
                      </SensitiveData>
                    </TableCell>
                    <TableCell>
                      {wip.nextBillingEventTriggerType === 'percent-complete' ? (
                        // % complete milestone — show threshold %, not a date
                        <div className="space-y-0.5">
                          <div className="text-sm font-medium">
                            At {wip.nextBillingEventTriggerPercent}% complete
                          </div>
                          {/* If there's also an optional deadline date, show it */}
                          {wip.nextBillingEvent && (
                            <div className="text-xs text-muted-foreground">
                              by {formatDate(wip.nextBillingEvent)}
                            </div>
                          )}
                          {wip.nextBillingEventAmount != null && (
                            <SensitiveData>
                              <div className="text-xs text-muted-foreground">
                                {formatCurrency(wip.nextBillingEventAmount)}
                              </div>
                            </SensitiveData>
                          )}
                        </div>
                      ) : wip.nextBillingEvent ? (
                        // Date-based milestone — show the date as before
                        <div className="space-y-0.5">
                          <div className="text-sm">{formatDate(wip.nextBillingEvent)}</div>
                          {wip.nextBillingEventAmount != null && (
                            <SensitiveData>
                              <div className="text-xs text-muted-foreground">
                                {formatCurrency(wip.nextBillingEventAmount)}
                              </div>
                            </SensitiveData>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>{getAgingBadge(wip.agingDays)}</TableCell>
                    <TableCell>
                      {wip.readyToBill ? (
                        <Badge variant="success">
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          Ready to Bill
                        </Badge>
                      ) : wip.overBilling > 0 ? (
                        <Badge variant="destructive">Over Billed</Badge>
                      ) : (
                        <Badge variant="secondary">In Progress</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Insights */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">💡 WIP Insights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="h-2 w-2 rounded-full bg-success-600 mt-2" />
              <div>
                <p className="text-sm font-medium">Ready for Invoicing</p>
                <p className="text-xs text-muted-foreground">
                  {readyToBillRows.length} project{readyToBillRows.length !== 1 ? 's are' : ' is'} ready to bill
                  {readyToBillAmount > 0 && (
                    <>
                      {' '}totalling{' '}
                      <SensitiveData>
                        {formatCurrency(readyToBillAmount)}
                      </SensitiveData>
                    </>
                  )}
                </p>
              </div>
            </div>
            {billingDueThisMonth.count > 0 && (
              <div className="flex items-start gap-3">
                <div className="h-2 w-2 rounded-full bg-warning-500 mt-2" />
                <div>
                  <p className="text-sm font-medium">Billing Events Due This Month</p>
                  <p className="text-xs text-muted-foreground">
                    {billingDueThisMonth.count} billing event{billingDueThisMonth.count !== 1 ? 's' : ''} totalling{' '}
                    <SensitiveData>
                      {formatCurrency(billingDueThisMonth.total)}
                    </SensitiveData>
                  </p>
                </div>
              </div>
            )}
            {totalUnderBilling > 50000 && (
              <div className="flex items-start gap-3">
                <div className="h-2 w-2 rounded-full bg-warning-600 mt-2" />
                <div>
                  <p className="text-sm font-medium">Under Billing Alert</p>
                  <p className="text-xs text-muted-foreground">
                    <SensitiveData>
                      {formatCurrency(totalUnderBilling)}
                    </SensitiveData>{' '}
                    in unbilled work - consider invoicing
                  </p>
                </div>
              </div>
            )}
            {totalOverBilling > 0 && (
              <div className="flex items-start gap-3">
                <div className="h-2 w-2 rounded-full bg-error-600 mt-2" />
                <div>
                  <p className="text-sm font-medium">Over Billing Risk</p>
                  <p className="text-xs text-muted-foreground">
                    <SensitiveData>
                      {formatCurrency(totalOverBilling)}
                    </SensitiveData>{' '}
                    billed ahead of work completion
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">📋 Month-End Checklist</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-success-600" />
                <span className="text-sm">Review all WIP amounts</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-success-600" />
                <span className="text-sm">Verify costs are approved</span>
              </div>
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-warning-600" />
                <span className="text-sm">Process ready-to-bill projects</span>
              </div>
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-warning-600" />
                <span className="text-sm">Reconcile with Access Financials</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm">Generate journal entries</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
